/**
 * Age-band options used everywhere a user targets children by age: the hero
 * search bar, the madlib sentence, and the impact map's Age control.
 *
 * These ids line up 1:1 with the API's `brackets` ACS bins (`AgeBracket`
 * below), so a selection is sent straight through via `bracketsFromAgeIds` —
 * no more legacy `start_age`/`fraction` translation.
 */

/** The API's PIC ACS bins (`AgeBracket` in the OpenAPI schema). */
export type AgeBracket = 'all_children' | 'under_5' | '5_to_9' | '10_to_14' | '15_to_17';

export interface AgeRange {
  /** Stable identifier used as the underlying select value — an `AgeBracket`. */
  id: AgeBracket;
  /** Human-readable label. */
  label: string;
  /** Lowest age in the band, inclusive. `undefined` for the "all ages" option. */
  min?: number;
  /** Highest age in the band, inclusive. `undefined` for the "all ages" option. */
  max?: number;
}

export const AGE_RANGES: readonly AgeRange[] = [
  { id: 'all_children', label: 'All ages' },
  { id: 'under_5', label: 'Under 5', min: 0, max: 4 },
  { id: '5_to_9', label: '5-9', min: 5, max: 9 },
  { id: '10_to_14', label: '10-14', min: 10, max: 14 },
  { id: '15_to_17', label: '15-17', min: 15, max: 17 },
] as const;

/** The exclusive "all children" option. */
export const ALL_AGES_ID: AgeRange['id'] = 'all_children';

/** The default multi-select value when nothing is chosen. */
export const DEFAULT_AGE_SELECTION: readonly string[] = [ALL_AGES_ID];

/**
 * Reconcile a new multi-select value against the previous one so that
 * "all ages" is mutually exclusive with the specific ranges:
 *   - picking "all" clears any ranges,
 *   - picking a range clears "all",
 *   - clearing a specific range down to nothing falls back to "all",
 *   - unchecking "all ages" itself (the sole selection) leaves an explicit
 *     empty selection rather than snapping back to checked — empty and
 *     `[ALL_AGES_ID]` are already treated identically everywhere this is
 *     read (`bracketsFromAgeIds`, `ageSelectionLabel`), so this just lets
 *     the checkbox actually respond to the click instead of appearing stuck.
 */
export function resolveAgeSelection(prev: string[], next: string[]): string[] {
  const added = next.filter((v) => !prev.includes(v));
  if (added.includes(ALL_AGES_ID)) {
    return [ALL_AGES_ID];
  }
  if (next.includes(ALL_AGES_ID) && added.length > 0) {
    return next.filter((v) => v !== ALL_AGES_ID);
  }
  if (prev.length === 1 && prev[0] === ALL_AGES_ID && next.length === 0) {
    return [];
  }
  return next.length ? next : [ALL_AGES_ID];
}

/** Human label for an age multi-selection ("All ages" or "0-3, 4-7"). */
export function ageSelectionLabel(ids: string[]): string {
  if (!ids.length || ids.includes(ALL_AGES_ID)) return 'All ages';
  return ids
    .map((id) => AGE_RANGES.find((r) => r.id === id)?.label)
    .filter(Boolean)
    .join(', ');
}

/**
 * A multi-select of age-range ids → the API's `brackets` array. Empty or
 * "all children" both mean "every bin" per the API contract, so either maps
 * to `[]` (the API's own default) rather than to `['all_children']`.
 */
export function bracketsFromAgeIds(ids: string[]): AgeBracket[] {
  if (!ids.length || ids.includes(ALL_AGES_ID)) return [];
  return ids.filter((id): id is AgeBracket =>
    AGE_RANGES.some((r) => r.id === id && r.id !== ALL_AGES_ID),
  );
}
